import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip session probe on truly public magic-link routes (no need to attempt auth)
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (/^\/welcome(\/|$)/.test(path)) {
      setUser(false);
      return;
    }
    if (/^\/setup(\/|$)/.test(path)) {
      // Magic-link setup page is fully public; skip the auth probe so a 401
      // doesn't flash a redirect loop before the recipient sets their password.
      setUser(false);
      return;
    }
    if (/^\/preboarding(\/|$)/.test(path)) {
      // Candidate pre-onboarding portal is fully public, token-gated.
      setUser(false);
      return;
    }
    if (/^\/client(\/|$)/.test(path)) {
      // Client Portal uses a separate JWT (saarone_client_token) — the
      // agency auth probe must not trigger here.
      setUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("hrms_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { sessionStorage.removeItem("setup:progress"); } catch { /* ignore */ }
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("hrms_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function routeForRole(role) {
  switch (role) {
    case "partner_admin": return "/partner";
    case "super_admin": return "/app/platform";
    case "reseller": return "/app/reseller";
    case "company_admin":
    case "country_head":
    case "region_head": return "/app/hr";
    case "branch_manager":
    case "sub_manager":
    case "assistant_manager": return "/app/manager";
    case "employee":
    default: return "/app/employee";
  }
}
